import {useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'sonner';
import {Button, ErrorPlaceholder, JobLoader} from '@tinct/web-components';
import {useFunnel} from '@tinct/web-funnel';
import {useStaticAssetsUrl} from '@tinct/core-utils';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {CampaignCreationFunnelState} from '../../campaign-creation-funnel/campaign-creation-funnel';
import {usePublishingJob} from './hooks/use-publishing-job';

export const Step8Publishing = () => {
  const {
    state: {campaign},
  } = useFunnel<CampaignCreationFunnelState>();
  const campaignId = campaign.id;

  if (!campaignId) {
    return <div>No campaign ID found.</div>;
  }

  return <Step8PublishingContent campaignId={campaignId} />;
};

const Step8PublishingContent = ({campaignId}: {campaignId: string}) => {
  const navigate = useNavigate();
  const currentWorkspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const assetsUrl = useStaticAssetsUrl();
  const hasNavigatedRef = useRef(false);

  const {subJobs, allCompleted, isPublishFailed, retryPublish, gameModal} = usePublishingJob(campaignId);

  // Once publishing completes, the funnel ends on the campaign dashboard
  // (Page variants tab) with a success toast — there is no "live" step anymore.
  useEffect(() => {
    if (allCompleted && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      toast.success('Campaign published');
      navigate(
        `${currentWorkspaceUrlPrefix}/campaigns/${campaignId}/landing-pages`,
        {replace: true},
      );
    }
  }, [allCompleted, campaignId, currentWorkspaceUrlPrefix, navigate]);

  if (isPublishFailed) {
    return (
      <ErrorPlaceholder
        image={
          <img
            src={`${assetsUrl}/error-analyze.png`}
            alt=""
            className="h-[140px] w-[280px] object-cover"
          />
        }
        title="Campaign publishing failed"
        description="We couldn't publish your campaign. Please try again — if the problem persists, contact support."
        actions={
          <>
            <Button size="sm" onClick={retryPublish}>
              Retry publishing
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href="mailto:support@tinct.ai">Contact support</a>
            </Button>
          </>
        }
      />
    );
  }

  return (
    <div className="container mx-auto flex w-full flex-col items-center justify-center">
      <div className="flex w-full max-w-xl flex-col gap-8">
        <div className="flex items-end gap-4">
          <div className="flex flex-1 flex-col gap-2">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-card-foreground">
              Launching your campaign...
            </h2>
          </div>
          <div className="flex shrink-0 items-end">
            <img
              src={`${assetsUrl}/run-sampling.png`}
              alt=""
              className="w-[100px]"
            />
          </div>
        </div>

        <JobLoader subJobs={subJobs} />
      </div>
      {gameModal}
    </div>
  );
};
